// src/components/sections/ClientHeader.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import { getStatusColor } from '../../utils/colors';

const getSatisfactionColor = (score) => {
  if (score === null || score === undefined) return '#757575';
  if (score >= 4.5) return '#03DAC6';
  if (score >= 4.0) return '#64B5F6';
  if (score >= 3.5) return '#FFCF44';
  if (score >= 3.0) return '#FF9800';
  return '#CF6679';
};

const ClientHeader = ({ industry, status, location, fullAddress, revenue, employees, clientSatisfaction }) => {
  const getGoogleMapsUrl = (address) => {
    if (!address) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };
  const mapsUrl = getGoogleMapsUrl(fullAddress);

  return (
    <Box sx={{ p: 1.5, bgcolor: '#1A1A1A', display: 'flex', flexDirection: 'column', gap: 0.75 }}>

      {/* Row 1 — industry + status/CSAT chips */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <BusinessIcon sx={{ fontSize: '0.9rem', color: '#64B5F6' }} />
          <Typography sx={{ fontSize: '0.75rem', color: '#03DAC6', fontWeight: 'bold' }}>
            {industry}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={status}
            size="small"
            sx={{ fontSize: '0.65rem', bgcolor: `${getStatusColor(status)}30`, color: getStatusColor(status), height: 20 }}
          />
          {typeof clientSatisfaction === 'number' && (
            <Chip
              label={`CSAT: ${clientSatisfaction.toFixed(1)}`}
              size="small"
              sx={{ fontSize: '0.65rem', bgcolor: `${getSatisfactionColor(clientSatisfaction)}20`, color: getSatisfactionColor(clientSatisfaction), height: 20, fontWeight: 'bold' }}
            />
          )}
        </Box>
      </Box>

      {/* Row 2 — location + employees */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {location && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: mapsUrl ? 'pointer' : 'default', '&:hover': mapsUrl ? { opacity: 0.8 } : {} }}
            onClick={mapsUrl ? () => window.open(mapsUrl, 'GoogleMaps', 'width=800,height=600,scrollbars=yes,resizable=yes') : undefined}
            title={mapsUrl ? `Open in Google Maps` : location}
          >
            <LocationOnIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: '#64B5F6' }} />
            <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {location}
            </Typography>
          </Box>
        )}
        {employees && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ fontSize: '0.85rem', mr: 0.5, color: '#64B5F6' }} />
            <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {employees.toLocaleString()} employees
            </Typography>
          </Box>
        )}
      </Box>

    </Box>
  );
};

ClientHeader.propTypes = {
  industry: PropTypes.string.isRequired,
  status: PropTypes.string,
  location: PropTypes.string,
  fullAddress: PropTypes.string,
  revenue: PropTypes.string,
  employees: PropTypes.number,
  clientSatisfaction: PropTypes.number
};

export default ClientHeader;
